/**
 * Matter Status Configuration
 *
 * Single source of truth for all matter status-related data:
 * - Status ordering for dropdowns and display
 * - Status groupings (active, treatment, demand, terminal)
 * - Status styling (colors, borders)
 *
 * Following the configuration-driven architecture pattern from tasks-datatable.
 */

import { MatterStatus } from '@/services/types/matter-types';

// =============================================================================
// STATUS ORDERING
// =============================================================================

/**
 * Canonical ordered list of matter statuses for consistent display.
 * Derived from enum definition order - the enum IS the source of truth.
 * Used in dropdowns, status selectors, and anywhere statuses need ordering.
 */
export const ORDERED_MATTER_STATUSES = Object.values(MatterStatus) as readonly MatterStatus[];

// =============================================================================
// STATUS GROUPINGS
// =============================================================================

/**
 * Terminal matter statuses that require a note explaining the reason for the change.
 * These are statuses where the matter is effectively closed/ended.
 * Defined first since ACTIVE_MATTER_STATUSES derives from this.
 */
export const TERMINAL_MATTER_STATUSES: readonly MatterStatus[] = [
  MatterStatus.COMPLETE,
  MatterStatus.HIDDEN,
  MatterStatus.CLIENT_DROPPED,
  MatterStatus.RETURNED_TO_FIRM,
  MatterStatus.INGESTED_IN_ERROR,
] as const;

/**
 * Statuses that are system-managed and should not appear in user-facing
 * status dropdowns (bulk change, single-matter change, create form).
 * INTAKE matters are auto-created during intake flow — users never set this manually.
 */
export const SYSTEM_ONLY_MATTER_STATUSES: readonly MatterStatus[] = [
  MatterStatus.INTAKE,
  MatterStatus.HIDDEN,
] as const;

/**
 * Active matter statuses - matters that are actively being worked.
 * Derived as: all statuses except terminal and system-only ones.
 * Used for:
 * - "Active Matters" metric and default filter for all users
 * - "Active" quick filter preset in Case Status column
 *
 * Note: TO_BE_DROPPED is considered active (workable) - tasks from these
 * matters appear in task lists and counts.
 */
const excludedFromActive = new Set<MatterStatus>([
  ...TERMINAL_MATTER_STATUSES,
  ...SYSTEM_ONLY_MATTER_STATUSES,
]);
export const ACTIVE_MATTER_STATUSES = ORDERED_MATTER_STATUSES.filter(
  (s) => !excludedFromActive.has(s)
);

/**
 * Statuses that users can manually select in dropdowns.
 * Excludes system-only statuses (INTAKE) that are managed automatically.
 */
const systemOnlySet = new Set<MatterStatus>(SYSTEM_ONLY_MATTER_STATUSES);
export const USER_SELECTABLE_MATTER_STATUSES = ORDERED_MATTER_STATUSES.filter(
  (s) => !systemOnlySet.has(s),
);

/**
 * Treatment-related statuses (for metrics grouping)
 */
export const TREATMENT_STATUSES: MatterStatus[] = [
  MatterStatus.TREATMENT,
  MatterStatus.TREATMENT_COMPLETE,
];

/**
 * Demand-related statuses (for metrics grouping)
 */
export const DEMAND_STATUSES: MatterStatus[] = [
  MatterStatus.READY_FOR_DEMAND,
  MatterStatus.DEMAND_PREPARATION,
  MatterStatus.DEMAND_LETTER,
];

/**
 * Check if a status is a terminal status requiring a note
 */
export const isTerminalMatterStatus = (status: MatterStatus): boolean =>
  TERMINAL_MATTER_STATUSES.includes(status);

// =============================================================================
// STATUS STYLING CONFIGURATION
// =============================================================================

/**
 * Style configuration for each matter status.
 * All color classes in one place for consistency.
 */
interface MatterStatusStyle {
  /** Text-only color (for badges, inline text) */
  textColor: string;
  /** Full background + text styling (for dropdown items, pills) */
  bgColor: string;
  /** Hover-enabled background styling */
  bgColorHover: string;
  /** Border color (for kanban columns, cards) */
  borderColor: string;
}

const DEFAULT_STATUS_STYLE: MatterStatusStyle = {
  textColor: 'text-gray-600',
  bgColor: 'bg-gray-100 text-gray-600',
  bgColorHover: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
  borderColor: 'border-gray-200',
};

/**
 * Configuration-driven status styling.
 * Add new statuses here and all consumers automatically get the styling.
 */
export const MATTER_STATUS_STYLES: Record<MatterStatus, MatterStatusStyle> = {
  [MatterStatus.INTAKE]: {
    textColor: 'text-gray-500',
    bgColor: 'bg-gray-100 text-gray-600',
    bgColorHover: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
    borderColor: 'border-gray-200',
  },
  [MatterStatus.CASE_INGESTION]: {
    textColor: 'text-sky-700',
    bgColor: 'bg-sky-100 text-sky-800',
    bgColorHover: 'bg-sky-100 text-sky-800 hover:bg-sky-200',
    borderColor: 'border-sky-200',
  },
  [MatterStatus.INITIAL_REVIEW]: {
    textColor: 'text-cyan-700',
    bgColor: 'bg-cyan-100 text-cyan-800',
    bgColorHover: 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200',
    borderColor: 'border-cyan-200',
  },
  [MatterStatus.CASE_SETUP]: {
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-100 text-purple-800',
    bgColorHover: 'bg-purple-100 text-purple-800 hover:bg-purple-200',
    borderColor: 'border-purple-200',
  },
  [MatterStatus.TREATMENT]: {
    textColor: 'text-green-700',
    bgColor: 'bg-green-100 text-green-800',
    bgColorHover: 'bg-green-100 text-green-800 hover:bg-green-200',
    borderColor: 'border-green-200',
  },
  [MatterStatus.TREATMENT_COMPLETE]: {
    textColor: 'text-emerald-700',
    bgColor: 'bg-emerald-100 text-emerald-800',
    bgColorHover: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
    borderColor: 'border-emerald-200',
  },
  [MatterStatus.READY_FOR_DEMAND]: {
    textColor: 'text-orange-700',
    bgColor: 'bg-orange-100 text-orange-800',
    bgColorHover: 'bg-orange-100 text-orange-800 hover:bg-orange-200',
    borderColor: 'border-orange-200',
  },
  [MatterStatus.DEMAND_PREPARATION]: {
    textColor: 'text-red-700',
    bgColor: 'bg-red-100 text-red-800',
    bgColorHover: 'bg-red-100 text-red-800 hover:bg-red-200',
    borderColor: 'border-red-200',
  },
  [MatterStatus.DEMAND_LETTER]: {
    textColor: 'text-pink-700',
    bgColor: 'bg-pink-100 text-pink-800',
    bgColorHover: 'bg-pink-100 text-pink-800 hover:bg-pink-200',
    borderColor: 'border-pink-200',
  },
  [MatterStatus.LITIGATION]: {
    textColor: 'text-indigo-700',
    bgColor: 'bg-indigo-100 text-indigo-800',
    bgColorHover: 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200',
    borderColor: 'border-indigo-200',
  },
  [MatterStatus.COMPLETE]: {
    textColor: 'text-teal-700',
    bgColor: 'bg-teal-100 text-teal-800',
    bgColorHover: 'bg-teal-100 text-teal-800 hover:bg-teal-200',
    borderColor: 'border-teal-200',
  },
  [MatterStatus.HIDDEN]: {
    textColor: 'text-gray-600',
    bgColor: 'bg-gray-100 text-gray-600',
    bgColorHover: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
    borderColor: 'border-gray-200',
  },
  [MatterStatus.CLIENT_DROPPED]: {
    textColor: 'text-gray-700',
    bgColor: 'bg-gray-100 text-gray-800',
    bgColorHover: 'bg-gray-100 text-gray-800 hover:bg-gray-200',
    borderColor: 'border-gray-200',
  },
  [MatterStatus.TO_BE_DROPPED]: {
    textColor: 'text-slate-700',
    bgColor: 'bg-slate-100 text-slate-800',
    bgColorHover: 'bg-slate-100 text-slate-800 hover:bg-slate-200',
    borderColor: 'border-slate-200',
  },
  [MatterStatus.RETURNED_TO_FIRM]: {
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-100 text-amber-800',
    bgColorHover: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
    borderColor: 'border-amber-200',
  },
  [MatterStatus.INGESTED_IN_ERROR]: {
    textColor: 'text-red-700',
    bgColor: 'bg-red-100 text-red-800',
    bgColorHover: 'bg-red-100 text-red-800 hover:bg-red-200',
    borderColor: 'border-red-200',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get text color for a status (for badges, inline text).
 * Handles unknown statuses gracefully.
 */
export const getStatusTextColor = (status?: string): string => {
  if (!status) return DEFAULT_STATUS_STYLE.textColor;
  const style = MATTER_STATUS_STYLES[status as MatterStatus];
  return style?.textColor ?? DEFAULT_STATUS_STYLE.textColor;
};

/**
 * Get full background + text styling for a status (for dropdown items, pills).
 * Handles unknown statuses gracefully.
 */
export const getStatusBgColor = (status?: string): string => {
  if (!status) return DEFAULT_STATUS_STYLE.bgColor;
  const style = MATTER_STATUS_STYLES[status as MatterStatus];
  return style?.bgColor ?? DEFAULT_STATUS_STYLE.bgColor;
};

/**
 * Get hover-enabled background styling for a status.
 * Handles unknown statuses gracefully.
 */
export const getStatusBgColorHover = (status?: string): string => {
  if (!status) return DEFAULT_STATUS_STYLE.bgColorHover;
  const style = MATTER_STATUS_STYLES[status as MatterStatus];
  return style?.bgColorHover ?? DEFAULT_STATUS_STYLE.bgColorHover;
};

/**
 * Get border color for a status (for kanban columns, cards).
 * Handles unknown statuses gracefully.
 */
export const getStatusBorderColor = (status?: string): string => {
  if (!status) return DEFAULT_STATUS_STYLE.borderColor;
  const style = MATTER_STATUS_STYLES[status as MatterStatus];
  return style?.borderColor ?? DEFAULT_STATUS_STYLE.borderColor;
};
